import { useEffect, useMemo, useRef, useState } from 'react';
import useGameStore from '../store/gameStore';
import type { GameEventKind } from '../types/gameTypes';
import { useT } from '../i18n';
import { LedgerIcon, TrophyIcon } from './GameIcons';

const eventTone = (kind: GameEventKind) => {
  if (kind === 'GAIN') return 'text-emerald-300 border-emerald-500/30';
  if (kind === 'LOSS' || kind === 'BANKRUPTCY') return 'text-rose-300 border-rose-500/30';
  if (kind === 'RISK') return 'text-amber-300 border-amber-500/30';
  if (kind === 'EXPANSION') return 'text-violet-300 border-violet-500/30';
  return 'text-slate-300 border-slate-600/30';
};

/**
 * Single consolidated, collapsible activity ticker.
 * The ONLY activity feed in the app — no market wire duplicates elsewhere.
 */
export const ActivityTicker = () => {
  const { events, gameOver } = useGameStore();
  const t = useT();
  const [open, setOpen] = useState(false);
  const latest = events[events.length - 1] ?? null;
  const feedRef = useRef<HTMLDivElement | null>(null);

  const recent = useMemo(() => [...events].reverse().slice(0, 24), [events]);

  // Keep the newest entry in view while expanded.
  useEffect(() => {
    if (open && feedRef.current) feedRef.current.scrollTop = 0;
  }, [open, latest?.id]);

  return (
    <div className="w-full overflow-hidden rounded-xl border border-slate-700/60 bg-slate-950/80">
        {/* Collapsed single-line ticker */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex min-h-[36px] w-full items-center gap-3 px-3 py-1 text-left transition hover:bg-slate-800/60"
        >
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-cyan-400/60 bg-cyan-500/15 text-cyan-200">
            <LedgerIcon size={16} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[10px] font-bold uppercase tracking-[0.2em] text-cyan-300/80">
              Activity Ticker · {t('common.round')} {latest?.round ?? 1}
            </span>
            <span className={`block truncate text-sm font-semibold ${latest ? eventTone(latest.kind).split(' ')[0] : 'text-slate-300'}`}>
              {latest?.message ?? '…'}
            </span>
          </span>
          {gameOver && (
            <span className="flex shrink-0 items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-amber-300">
              <TrophyIcon size={12} /> {t('board.sessionClosed')}
            </span>
          )}
          <span className="shrink-0 text-xs font-bold text-slate-300">{open ? '–' : `+ ${events.length}`}</span>
        </button>

        {/* Expanded consolidated log */}
        {open && (
          <div ref={feedRef} className="max-h-[240px] space-y-1 overflow-y-auto border-t border-slate-700/60 px-3 py-2">
            {recent.length === 0 && <p className="py-2 text-xs text-slate-500">No activity yet.</p>}
            {recent.map((event) => (
              <div
                key={event.id}
                className={`flex items-start gap-2 rounded-lg border bg-slate-900/60 px-3 py-1.5 text-xs ${eventTone(event.kind)}`}
              >
                <span className="shrink-0 font-mono text-[10px] text-slate-500">R{event.round}</span>
                <span className="min-w-0 flex-1">{event.message}</span>
              </div>
            ))}
          </div>
        )}
    </div>
  );
};

export default ActivityTicker;